import React, { useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { useInventory } from '../../context/InventoryContext';

const CategoryBreakdownChart: React.FC = () => {
  const { getDashboardSummary, getCategoryColor } = useInventory();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const summary = getDashboardSummary();
    const categories = summary.categoryBreakdown.filter(c => c.count > 0);
    
    if (categories.length === 0) return;
    
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    const total = categories.reduce((sum, category) => sum + category.count, 0);
    let startAngle = 0;
    
    categories.forEach(category => {
      const sliceAngle = (2 * Math.PI * category.count) / total;
      const color = getCategoryColor(category.categoryId);
      
      // Draw slice
      ctx.beginPath();
      ctx.moveTo(100, 100);
      ctx.arc(100, 100, 80, startAngle, startAngle + sliceAngle);
      ctx.fillStyle = color;
      ctx.fill();
      
      // Calculate text position
      const midAngle = startAngle + sliceAngle / 2;
      
      // Only add label if slice is big enough
      if (category.count / total > 0.05) {
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${Math.round((category.count / total) * 100)}%`, 
          100 + 50 * Math.cos(midAngle), 
          100 + 50 * Math.sin(midAngle));
      }
      
      startAngle += sliceAngle;
    });
    
  }, [getDashboardSummary, getCategoryColor]);
  
  const summary = getDashboardSummary();
  const categories = summary.categoryBreakdown.filter(c => c.count > 0);
  
  return (
    <Card className="transition-all duration-200 hover:shadow-md flex flex-col h-full">
      <CardHeader>
        <CardTitle>Category Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="flex flex-col h-full">
          {/* Chart container with fixed height */}
          <div className="flex justify-center mb-4">
            <canvas ref={canvasRef} width="200" height="200" className="flex-shrink-0"></canvas>
          </div>
          
          {/* Category legend with auto height */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mt-2">
            {categories.map((category) => (
              <div key={category.categoryId} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span 
                    className="w-3 h-3 rounded-full mr-2 flex-shrink-0" 
                    style={{ backgroundColor: getCategoryColor(category.categoryId) }}
                  ></span>
                  <span className="text-sm text-gray-600 truncate max-w-[120px]">
                    {category.categoryName}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 ml-2">
                  {category.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoryBreakdownChart;